import {
  InfoOutlined,
  DeleteOutline,
  EditOutlined,
  Add,
  LightbulbOutlined,
} from '@mui/icons-material';
import { Button, CardContainer, Dropdown } from 'finara-components';
import type { DropdownOption } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Input } from '@/components/common/Input';
import { useRates, useCreateRate, useUpdateRate, useDeleteRate } from '@/hooks/queries/useRates';

export const RatesManagementCard: React.FC = () => {
  const { data: ratesResponse, isLoading } = useRates({ limit: 100 });
  const createRate = useCreateRate();
  const updateRate = useUpdateRate();
  const deleteRate = useDeleteRate();

  const [isEditing, setIsEditing] = useState(false);
  const [editingRateId, setEditingRateId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    value: 0,
    period: 'yearly' as 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly',
  });

  const rates = ratesResponse?.data || [];
  const hasMultipleRates = rates.length > 1;

  const periodOptions: DropdownOption[] = [
    { value: 'yearly', label: 'Anual' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'daily', label: 'Diario' },
    { value: 'hourly', label: 'Por Hora' },
  ];

  const calculateAnnualRate = (value: number, period: string): number => {
    switch (period) {
      case 'hourly': return value * 365 * 24;
      case 'daily': return value * 365;
      case 'weekly': return value * 52;
      case 'monthly': return value * 12;
      case 'yearly': return value;
      default: return value;
    }
  };

  const handleCreate = () => {
    setFormData({
      name: 'Tasa de Interés Estándar',
      description: 'Tasa predeterminada para créditos de empleados',
      value: 0.57,
      period: 'daily',
    });
    setEditingRateId(null);
    setIsEditing(true);
  };

  const handleEdit = (rateId: string) => {
    const rate = rates.find(r => r.id === rateId);
    if (rate) {
      setFormData({
        name: rate.name,
        description: rate.description,
        value: rate.value,
        period: rate.period,
      });
      setEditingRateId(rateId);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingRateId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) { toast.error('El nombre es requerido'); return; }
    if (!formData.description.trim()) { toast.error('La descripción es requerida'); return; }
    if (formData.value < 0) { toast.error('El valor no puede ser negativo'); return; }

    try {
      if (editingRateId) {
        await updateRate.mutateAsync({
          id: editingRateId,
          data: { ...formData, isActive: true },
        });
        toast.success('Tasa actualizada. Los nuevos créditos usarán esta tasa.');
      } else {
        await createRate.mutateAsync({ ...formData, isActive: true });
        toast.success('Tasa creada exitosamente');
      }
      setIsEditing(false);
      setEditingRateId(null);
    } catch (_error) {
      toast.error('Error al guardar la tasa');
    }
  };

  const handleDelete = async (rateId: string) => {
    if (
      !window.confirm(
        '⚠️ Los créditos existentes que usan esta tasa perderán su referencia.\n\n¿Estás seguro de eliminar esta tasa?'
      )
    ) return;

    try {
      await deleteRate.mutateAsync(rateId);
      toast.success('Tasa eliminada');
    } catch (_error) {
      toast.error('Error al eliminar la tasa');
    }
  };

  if (isLoading) {
    return (
      <CardContainer>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-white/10 rounded w-1/3"></div>
            <div className="h-4 bg-white/10 rounded w-2/3"></div>
          </div>
        </div>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-brand-white">Gestión de Tasas de Interés</h3>
            <p className="text-sm text-muted mt-1">
              Configura la tasa de interés personalizada para créditos
            </p>
          </div>
          {!isEditing && rates.length === 0 && (
            <Button variant="black" size="small" onClick={handleCreate}>
              <Add fontSize="small" className="mr-1" />
              Crear Tasa
            </Button>
          )}
        </div>

        {hasMultipleRates && (
          <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <InfoOutlined className="text-orange-400 mt-0.5" fontSize="small" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-300">⚠️ Múltiples Tasas Detectadas</p>
                <p className="text-xs text-orange-400 mt-1">
                  Tienes {rates.length} tasas configuradas, pero solo se usa la tasa marcada como
                  activa. Se recomienda mantener una sola tasa activa para evitar confusión.
                </p>
              </div>
            </div>
          </div>
        )}

        {isEditing ? (
          <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
            <div>
              <Input
                id="rateName"
                label="Nombre de la Tasa"
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Tasa de Interés Estándar"
                disabled={createRate.isPending || updateRate.isPending}
              />
            </div>

            <div>
              <label
                htmlFor="rateDescription"
                className="block text-sm font-medium text-brand-white mb-1"
              >
                Descripción
              </label>
              <textarea
                id="rateDescription"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe el propósito de esta tasa..."
                rows={3}
                className="w-full rounded-lg border border-border bg-surface px-4 py-2 text-sm text-brand-white placeholder:text-muted focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue disabled:opacity-50"
                disabled={createRate.isPending || updateRate.isPending}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  id="rateValue"
                  label="Valor de la Tasa"
                  type="number"
                  value={formData.value}
                  onChange={e => setFormData(prev => ({ ...prev, value: Number(e.target.value) }))}
                  step="0.1"
                  min="0"
                  placeholder="5.0"
                  disabled={createRate.isPending || updateRate.isPending}
                />
              </div>

              <div>
                <label
                  htmlFor="ratePeriod"
                  className="block text-sm font-medium text-brand-white mb-1"
                >
                  Período
                </label>
                <Dropdown
                  id="ratePeriod"
                  value={formData.period}
                  onChange={value =>
                    setFormData(prev => ({
                      ...prev,
                      period: String(value) as typeof formData.period,
                    }))
                  }
                  options={periodOptions}
                  disabled={createRate.isPending || updateRate.isPending}
                />
              </div>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <LightbulbOutlined className="text-brand-blue mt-0.5" fontSize="small" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-brand-white">
                    Equivalente Anual:{' '}
                    {calculateAnnualRate(formData.value, formData.period).toFixed(2)}%
                  </p>
                  <p className="text-xs text-muted mt-1">
                    {formData.value}%{' '}
                    {periodOptions.find(o => o.value === formData.period)?.label.toLowerCase()} ={' '}
                    {calculateAnnualRate(formData.value, formData.period).toFixed(2)}% anual
                  </p>
                </div>
              </div>
            </div>

            {editingRateId && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-xs text-yellow-400">
                  ⚠️ Actualizar esta tasa afectará todos los nuevos créditos. Los créditos
                  existentes mantendrán su tasa original.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                type="submit"
                variant="black"
                size="small"
                disabled={createRate.isPending || updateRate.isPending}
              >
                {createRate.isPending || updateRate.isPending
                  ? 'Guardando...'
                  : editingRateId
                    ? 'Actualizar Tasa'
                    : 'Crear Tasa'}
              </Button>
              <Button
                type="button"
                variant="white"
                size="small"
                onClick={handleCancel}
                disabled={createRate.isPending || updateRate.isPending}
              >
                Cancelar
              </Button>
            </div>
          </form>
        ) : rates.length === 0 ? (
          <div className="text-center py-8">
            <InfoOutlined className="text-muted mx-auto mb-3" style={{ fontSize: '3rem' }} />
            <p className="text-sm text-muted mb-4">
              No hay tasas personalizadas configuradas. Se usa la tasa predeterminada del sistema
              (0.57% diario).
            </p>
            <Button variant="black" size="small" onClick={handleCreate}>
              Crear Tasa Personalizada
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {rates.map(rate => (
              <div key={rate.id} className="p-4 border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-brand-white">{rate.name}</h4>
                      {rate.isActive ? (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                          Activa
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-white/10 text-muted text-xs rounded-full">
                          Inactiva
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted">{rate.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(rate.id)}
                      className="p-2 text-muted hover:text-brand-white hover:bg-white/5 rounded"
                      disabled={deleteRate.isPending}
                    >
                      <EditOutlined fontSize="small" />
                    </button>
                    <button
                      onClick={() => void handleDelete(rate.id)}
                      className="p-2 text-muted hover:text-red-400 hover:bg-red-500/10 rounded"
                      disabled={deleteRate.isPending}
                    >
                      <DeleteOutline fontSize="small" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div>
                    <p className="text-xs text-muted">Valor</p>
                    <p className="text-lg font-semibold text-brand-white">{rate.value}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Período</p>
                    <p className="text-lg font-semibold text-brand-white capitalize">
                      {periodOptions.find(o => o.value === rate.period)?.label}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-muted mt-3">
                  Creada: {new Date(rate.createdAt).toLocaleDateString('es-MX')}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </CardContainer>
  );
};
